//
// Copyright 2025 DXOS.org
//

import * as Schema from 'effect/Schema';

import { raise } from '@dxos/debug';
import { type EncodedReference, EntityStructure, isEncodedReference } from '@dxos/echo-protocol';
import { assertArgument, invariant } from '@dxos/invariant';
import { EID, EntityId, URI } from '@dxos/keys';
import {
  assumeType,
  decodeUint8ArrayFromJson,
  deepMapValues,
  encodeUint8ArrayToJson,
  isEncodedUint8Array,
  visitValues,
} from '@dxos/util';

import type * as Database from '../../Database.ts';
import type * as Obj from '../../Obj.ts';
import { getTypeAnnotation, getTypeURI, setTypename } from '../Annotation/index.ts';
import { defineHiddenProperty, typedJsonSerializer } from '../common/proxy/index.ts';
import {
  type AnyEntity,
  ATTR_PARENT,
  ATTR_TYPE,
  EntityKind,
  KindId,
  ParentId,
  setSchema,
  setType,
} from '../common/types/index.ts';
import { ATTR_META, EntityMetaSchema, SCALAR_META_FIELDS, isTagEntity } from '../common/types/meta.ts';
import { MetaId } from '../common/types/model-symbols.ts';
import {
  ATTR_DELETED,
  ATTR_RELATION_SOURCE,
  ATTR_RELATION_TARGET,
  ATTR_SELF_URI,
  ATTR_SELF_URI_LEGACY,
  ObjectDatabaseId,
  ObjectDeletedId,
  type ObjectJSON,
  RelationSourceDXNId,
  RelationSourceId,
  RelationTargetDXNId,
  RelationTargetId,
  SelfURIId,
  TypeMetaSchemaDXN,
  assertObjectModel,
} from '../Entity/index.ts';
import { Ref, type RefResolver, refFromEncodedReference, setRefResolver } from '../Ref/index.ts';

type DeepReplaceRef<T> =
  T extends Ref<any>
    ? EncodedReference
    : T extends object
      ? {
          [K in keyof T]: DeepReplaceRef<T[K]>;
        }
      : T;

type SerializedObject<T extends { id: string }> = {
  [K in keyof T]: DeepReplaceRef<T[K]>;
} & ObjectJSON;

/**
 * Converts object to it's JSON representation.
 */
export const objectToJSON = <T extends AnyEntity>(obj: T): SerializedObject<T> => {
  const typename = getTypeURI(obj);
  invariant(typename && typeof typename === 'string');
  return typedJsonSerializer.call(obj);
};

/**
 * Creates an object from it's json representation.
 * Performs schema validation.
 * References and schema will be resolvable if the `refResolver` is provided.
 * The function need to be async to support resolving the schema as well as the relation endpoints.
 *
 * @param jsonData - JSON representation of the object.
 * @param options.refResolver - Resolver for references.
 * @param options.uri - Override object URI.
 * @param options.database - Database to associate with the object.
 */
export const objectFromJSON = async (
  jsonData: unknown,
  {
    refResolver,
    uri,
    database,
    parent,
  }: { refResolver?: RefResolver; uri?: URI.URI; database?: Database.Database; parent?: Obj.Unknown } = {},
): Promise<AnyEntity> => {
  assumeType<ObjectJSON>(jsonData);
  assertArgument(typeof jsonData === 'object' && jsonData !== null, 'jsonData', 'expect object');
  assertArgument(typeof jsonData[ATTR_TYPE] === 'string', 'jsonData[ATTR_TYPE]', 'expected object to have a type');
  assertArgument(typeof jsonData.id === 'string', 'jsonData.id', 'expected object to have an id');

  const type = URI.make(jsonData[ATTR_TYPE]);
  const schema = await refResolver?.resolveSchema(type);
  invariant(schema === undefined || Schema.isSchema(schema));
  const decodedInput = restoreUint8Arrays(stripInternalJsonKeys(jsonData));

  let obj: any;
  if (schema != null) {
    obj = await Schema.decodeUnknownPromise(schema)(decodedInput);
    if (refResolver) {
      setRefResolverOnData(obj, refResolver);
    }
  } else {
    obj = decodeGeneric(decodedInput, { refResolver });
  }

  invariant(EntityId.isValid(obj.id), 'Invalid object id');
  setTypename(obj, type);
  if (schema) {
    setSchema(obj, schema);
  }
  // Resolve and stamp the source type entity, if the resolver provides one.
  // Lets `Obj.getType` / `Entity.getType` return a stable entity for objects
  // loaded via `Obj.fromJSON` (serializer / queue paths).
  if (refResolver?.resolveType) {
    const typeEntity = await refResolver.resolveType(type);
    if (typeEntity != null) {
      setType(obj, typeEntity);
    }
  }

  const isRelation =
    typeof jsonData[ATTR_RELATION_SOURCE] === 'string' || typeof jsonData[ATTR_RELATION_TARGET] === 'string';
  if (isRelation) {
    const sourceDxn = jsonData[ATTR_RELATION_SOURCE] ?? raise(new TypeError('Missing relation source'));
    const targetDxn = jsonData[ATTR_RELATION_TARGET] ?? raise(new TypeError('Missing relation target'));

    const source = (await refResolver?.resolveLegacy(sourceDxn)) as AnyEntity | undefined;
    const target = (await refResolver?.resolveLegacy(targetDxn)) as AnyEntity | undefined;

    defineHiddenProperty(obj, KindId, EntityKind.Relation);
    defineHiddenProperty(obj, RelationSourceDXNId, sourceDxn);
    defineHiddenProperty(obj, RelationTargetDXNId, targetDxn);
    defineHiddenProperty(obj, RelationSourceId, source);
    defineHiddenProperty(obj, RelationTargetId, target);
  } else {
    // Honour the schema's TypeAnnotation kind — persisted `Type.Type` entities
    // (e.g. dynamic schemas loaded from a snapshot import) must brand as
    // `KindId = Type`, not Object, otherwise `Filter.type(Type.Type)` /
    // `Type.isType` skip them and the schema registry never picks them up.
    // Mirrors the kind resolution in `createObject` (the in-memory path).
    const annotationKind = schema != null ? getTypeAnnotation(schema)?.kind : undefined;
    defineHiddenProperty(obj, KindId, annotationKind === EntityKind.Type ? EntityKind.Type : EntityKind.Object);
  }

  if (typeof jsonData[ATTR_META] === 'object') {
    const { tags: encodedTags, ...encodedMeta } = normalizeMeta(jsonData[ATTR_META]);
    const meta = await Schema.decodeUnknownPromise(EntityMetaSchema)({ ...encodedMeta, tags: [] });
    invariant(Array.isArray(meta.keys));
    defineHiddenProperty(obj, MetaId, { ...meta, tags: await resolveTags(encodedTags, refResolver) });
  } else {
    defineHiddenProperty(obj, MetaId, {
      keys: [],
      tags: [],
      annotations: {},
    });
  }

  if (jsonData[ATTR_PARENT]) {
    const parentDxn = jsonData[ATTR_PARENT];
    const resolvedParent = (await refResolver?.resolveLegacy(parentDxn)) as Obj.Unknown | undefined;
    defineHiddenProperty(obj, ParentId, resolvedParent);
  } else if (parent) {
    defineHiddenProperty(obj, ParentId, parent);
  }

  if (uri) {
    defineHiddenProperty(obj, SelfURIId, uri);
  }

  if (database) {
    defineHiddenProperty(obj, ObjectDatabaseId, database);
  }

  // Carry the deletion marker so a tombstone snapshot (e.g. a feed item removed via `Feed.remove`)
  // hydrates with `Obj.isDeleted === true` rather than as a live object.
  defineHiddenProperty(obj, ObjectDeletedId, jsonData[ATTR_DELETED] === true);

  assertObjectModel(obj);
  invariant((obj as any)[ATTR_TYPE] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_META] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_DELETED] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_SELF_URI] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_SELF_URI_LEGACY] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_RELATION_SOURCE] === undefined, 'Invalid object model');
  invariant((obj as any)[ATTR_RELATION_TARGET] === undefined, 'Invalid object model');

  return obj;
};

/**
 * Resolves the encoded tag references of a serialized meta to {@link Tag} entities. A tag the
 * resolver cannot supply is dropped, as an unresolved parent is: `meta.tags` holds entities only.
 */
const resolveTags = async (encodedTags: readonly unknown[], refResolver: RefResolver | undefined) => {
  if (refResolver == null || encodedTags.length === 0) {
    return [];
  }
  const resolved = await Promise.all(
    encodedTags.map((tag) => (isEncodedReference(tag) ? refResolver.resolveLegacy(URI.make(tag['/'])) : undefined)),
  );
  return resolved.filter(isTagEntity);
};

/**
 * Backfills required meta fields and upgrades legacy `tags` (bare URI strings) to encoded references
 * so serialized data produced before the tags-as-refs migration still decodes.
 */
const normalizeMeta = (meta: any): any => {
  const tags = Array.isArray(meta?.tags)
    ? meta.tags.map((tag: unknown) => (typeof tag === 'string' ? { '/': URI.make(tag) } : tag))
    : [];
  // Coalesce required fields so explicit `undefined` in legacy input doesn't override the defaults.
  return {
    ...meta,
    keys: Array.isArray(meta?.keys) ? meta.keys : [],
    tags,
    annotations: meta?.annotations ?? {},
  };
};

const decodeGeneric = (jsonData: unknown, options: { refResolver?: RefResolver }) => {
  const props = stripInternalJsonKeys(jsonData);

  return deepMapValues(props, (value, visitor) => {
    if (isEncodedReference(value)) {
      return refFromEncodedReference(value, options.refResolver);
    }
    if (isEncodedUint8Array(value)) {
      return decodeUint8ArrayFromJson(value);
    }

    return visitor(value);
  });
};

/**
 * Recursively replaces encoded `Uint8Array` JSON markers with actual `Uint8Array` instances.
 * Runs before schema decoding so `Schema.Uint8Array` sees real bytes.
 */
const restoreUint8Arrays = (data: unknown): any =>
  deepMapValues(data, (value, recurse) => {
    if (isEncodedUint8Array(value)) {
      return decodeUint8ArrayFromJson(value);
    }
    return recurse(value);
  });

const stripInternalJsonKeys = (jsonData: unknown) => {
  const {
    [ATTR_TYPE]: _type,
    [ATTR_META]: _meta,
    [ATTR_DELETED]: _deleted,
    [ATTR_SELF_URI]: _selfUri,
    [ATTR_SELF_URI_LEGACY]: _legacySelfUri,
    [ATTR_RELATION_SOURCE]: _relationSource,
    [ATTR_RELATION_TARGET]: _relationTarget,
    ...props
  } = jsonData as any;

  return props;
};

export const setRefResolverOnData = (obj: AnyEntity, refResolver: RefResolver) => {
  const visitor = (value: unknown) => {
    if (Ref.isRef(value)) {
      setRefResolver(value, refResolver);
    } else {
      visitValues(value, visitor);
    }
  };

  visitor(obj);
};

/**
 * Convert EntityStructure to JSON data for indexing.
 * Different from {@link objectToJSON} as it takes the internal {@link EntityStructure} representation directly
 */
export const objectStructureToJson = (objectId: EntityId, structure: EntityStructure): Obj.JSON => {
  const typeRef = EntityStructure.getTypeReference(structure)?.['/'];
  const parent = EntityStructure.getParent(structure)?.['/'];
  const source = EntityStructure.getRelationSource(structure)?.['/'];
  const target = EntityStructure.getRelationTarget(structure)?.['/'];
  // Included so the indexer sees the meta section (notably `convergenceKey`, the merge trigger) —
  // matching the feed path, whose blocks carry `@meta` wholesale.
  const meta = structure.meta;
  const metaNotEmpty =
    meta !== undefined &&
    (meta.keys?.length > 0 ||
      (meta.tags?.length ?? 0) > 0 ||
      (meta.annotations !== undefined && Object.keys(meta.annotations).length > 0) ||
      SCALAR_META_FIELDS.some((field) => (meta as Record<string, unknown>)[field] !== undefined));
  return {
    ...encodeBytes(structure.data),
    id: objectId,
    [ATTR_META]: metaNotEmpty ? (meta as Obj.JSON[typeof ATTR_META]) : undefined,
    [ATTR_TYPE]: typeRef ? URI.make(typeRef) : undefined,
    [ATTR_DELETED]: EntityStructure.isDeleted(structure),
    [ATTR_PARENT]: parent !== undefined ? EID.tryParse(parent) : undefined,
    [ATTR_RELATION_SOURCE]: source !== undefined ? EID.tryParse(source) : undefined,
    [ATTR_RELATION_TARGET]: target !== undefined ? EID.tryParse(target) : undefined,
  };
};

/**
 * Bytes in the DAG-JSON `{ '/': { bytes } }` form the feed serializer writes, which plain JSON would flatten.
 * Only arrays and plain objects are walked, so a document's raw strings keep the `toJSON` that writes them as text.
 */
const encodeBytes = <T>(value: T): T =>
  (value instanceof Uint8Array
    ? encodeUint8ArrayToJson(value)
    : Array.isArray(value)
      ? value.map(encodeBytes)
      : typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
        ? Object.fromEntries(Object.entries(value).map(([key, item]) => [key, encodeBytes(item)]))
        : value) as T;

const STRUCTURE_JSON_KEYS: ReadonlySet<string> = new Set([
  'id',
  ATTR_TYPE,
  ATTR_META,
  ATTR_DELETED,
  ATTR_PARENT,
  ATTR_RELATION_SOURCE,
  ATTR_RELATION_TARGET,
]);

/**
 * Inverse of {@link objectStructureToJson}, for reading an object from the index's JSON. The JSON carries
 * no `system.kind`, so it is inferred from the relation endpoints and the type; nor the merge fields, and
 * `createdAt` comes from the caller.
 */
export const objectStructureFromJson = (
  json: Obj.JSON,
  { createdAt }: { createdAt?: number } = {},
): EntityStructure => {
  const record = json as Record<string, unknown>;
  const data = Object.fromEntries(
    Object.entries(record)
      .filter(([key]) => !STRUCTURE_JSON_KEYS.has(key))
      .map(([key, value]) => [
        key,
        deepMapValues(value, (value, recurse) =>
          isEncodedUint8Array(value) ? decodeUint8ArrayFromJson(value) : recurse(value),
        ),
      ]),
  );
  const reference = (value: unknown): EncodedReference | undefined =>
    typeof value === 'string' ? { '/': URI.make(value) } : undefined;
  const type = reference(record[ATTR_TYPE]);
  const source = reference(record[ATTR_RELATION_SOURCE]);
  const target = reference(record[ATTR_RELATION_TARGET]);
  const parent = reference(record[ATTR_PARENT]);
  const meta = record[ATTR_META] as Partial<EntityStructure['meta']> | undefined;
  return {
    system: {
      kind:
        source || target
          ? EntityKind.Relation
          : type?.['/'] === TypeMetaSchemaDXN.toString()
            ? EntityKind.Type
            : EntityKind.Object,
      ...(type && { type }),
      ...(parent && { parent }),
      ...(source && { source }),
      ...(target && { target }),
      ...(record[ATTR_DELETED] === true && { deleted: true }),
      ...(createdAt !== undefined && { createdAt }),
    },
    meta: { ...meta, keys: meta?.keys ?? [] },
    data,
  };
};
